import React, { useState, useEffect, useCallback } from 'react';
import { Megaphone, RefreshCw, ArrowLeft } from 'lucide-react';
import { announcementService, categoryVisual } from '../../services/announcement.service';
import './AnnouncementsPage.css';

const fmt = (v) =>
    v ? new Date(v).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

/**
 * Duyurular & Kampanyalar.
 *
 * Detay ayrı bir sayfa değil, aynı ekranda seçili kayıt: filo paneli hash tabanlı gezinme
 * kullanıyor ve duyuru id'si başına rota açmak gezinme sözlüğünü şişirirdi.
 *
 * Okunmuş duyuru listede KALIR; okundu damgası yalnızca rozeti ve açılış popup'ını etkiler.
 */
export default function AnnouncementsPage() {
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await announcementService.list();
        if (res?.success && res.data) {
            setItems(res.data.items || []);
            setUnreadCount(res.data.unreadCount || 0);
        } else {
            setItems([]);
            setUnreadCount(0);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const open = async (item) => {
        setSelected(item);

        if (!item.isRead) {
            await announcementService.markRead(item.id);
            // Yerel durumu da güncelle: listeye dönünce rozet hemen düşsün.
            setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, isRead: true } : x)));
            setUnreadCount((n) => Math.max(0, n - 1));
        }
    };

    if (selected) {
        const visual = categoryVisual(selected.category);

        return (
            <div className="fan-page">
                <button className="fan-back" onClick={() => setSelected(null)}>
                    <ArrowLeft size={15} /> Duyurulara dön
                </button>

                {selected.imageUrl && (
                    <img className="fan-detail-image" src={selected.imageUrl} alt="" />
                )}

                <span className="fan-badge" style={{ background: visual.bg, color: visual.color }}>
                    {selected.categoryText || visual.label}
                </span>

                <h1 className="fan-detail-title">{selected.title}</h1>
                <div className="fan-detail-date">{fmt(selected.publishedAtUtc)}</div>
                <p className="fan-detail-summary">{selected.summary}</p>

                <div className="fan-divider" />

                {/* Düz metin: satır sonları korunur, HTML yorumlanmaz. */}
                <div className="fan-detail-body">{selected.body}</div>
            </div>
        );
    }

    return (
        <div className="fan-page">
            <div className="fan-header">
                <span className="fan-header-icon"><Megaphone size={19} /></span>

                <div className="fan-header-text">
                    <h1>Duyurular</h1>
                    <p>Haberler, kampanyalar ve yenilikler</p>
                </div>

                {unreadCount > 0 && <span className="fan-unread">{unreadCount} okunmamış</span>}

                <button className="fan-refresh" onClick={load} disabled={loading}>
                    <RefreshCw size={15} className={loading ? 'fan-spin' : ''} /> Yenile
                </button>
            </div>

            {loading ? (
                <div className="fan-state"><RefreshCw size={17} className="fan-spin" /> Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="fan-state fan-empty">
                    <Megaphone size={28} />
                    <span>Henüz duyuru yok.</span>
                </div>
            ) : (
                <div className="fan-list">
                    {items.map((item) => {
                        const visual = categoryVisual(item.category);

                        return (
                            <button key={item.id} className="fan-card" onClick={() => open(item)}>
                                {item.imageUrl && <img className="fan-card-image" src={item.imageUrl} alt="" />}

                                <div className="fan-card-main">
                                    <div className="fan-card-top">
                                        <span className="fan-badge" style={{ background: visual.bg, color: visual.color }}>
                                            {item.categoryText || visual.label}
                                        </span>
                                        {!item.isRead && <span className="fan-dot" />}
                                    </div>

                                    <h3>{item.title}</h3>
                                    <p>{item.summary}</p>
                                    <span className="fan-card-date">{fmt(item.publishedAtUtc)}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
